import {
	type PropsWithChildren,
	type ReactNode,
	useEffect,
	useState,
} from 'react';

/**
 *	Returns true when the given media query matches.
 *
 *	@param query string Media query string, e.g. "(min-width: 60rem)"
 *	@returns boolean
 */
export function useMediaQuery(query: string): boolean {
	const [matches, setMatches] = useState(false);

	useEffect(() => {
		const mql = window.matchMedia(query);

		setMatches(mql.matches);

		const onChange = (event: MediaQueryListEvent) => {
			setMatches(event.matches);
		};

		mql.addEventListener('change', onChange);

		return () => mql.removeEventListener('change', onChange);
	}, [query]);

	return matches;
}

const MediaQueryBreakpoint = '600px';

interface MediaQueryProviderProps extends PropsWithChildren {
	breakpoint?: string;
	threshold?: 'min' | 'max';

	onDidMatch?: () => void;
}

export function MediaQuery({
	breakpoint,
	threshold,

	onDidMatch,

	children,
}: MediaQueryProviderProps): ReactNode {
	const shouldBreak = useMediaQuery(
		`(${threshold}-width: calc(${breakpoint} - 1px))`
	);

	useEffect(() => {
		if (shouldBreak && onDidMatch) {
			onDidMatch();
		}
	}, [shouldBreak]);

	if (!shouldBreak) {
		return null;
	}

	return children;
}

export function MinMediaQuery({
	breakpoint = MediaQueryBreakpoint,

	onDidMatch,

	children,
}: MediaQueryProviderProps): ReactNode {
	return (
		<MediaQuery threshold="min" breakpoint={breakpoint} onDidMatch={onDidMatch}>
			{children}
		</MediaQuery>
	);
}

export function MaxMediaQuery({
	breakpoint = MediaQueryBreakpoint,

	onDidMatch,

	children,
}: MediaQueryProviderProps): ReactNode {
	return (
		<MediaQuery threshold="max" breakpoint={breakpoint} onDidMatch={onDidMatch}>
			{children}
		</MediaQuery>
	);
}
